
import { optionTaxTransactionCode } from "@/app/infrastructures/misc/Utils";
import { OptionsClass } from "@/domain/entities/MainApp";
import { Options, prop, Vue } from "vue-class-component";
import UploadFile from "@/app/ui/components/upload-file/index.vue";
import Checkbox from "primevue/checkbox";
import {
  TaxValidationConfig,
  validateTaxRegistration
} from "@/domain/client/validation/";

export type TaxSectionProp = {
  taxNumber: string;
  NIK: string;
  NIKName: string;
  NIKAddress: string;
  transactionCode: OptionsClass;
  isEfakturWithNik: boolean;
  attachment: string;
  attachmentUrl: string;
  fileNameAttachment: string;
  taxName: string;
  taxAddress: string;
};

export type TaxSectionErrorProp = {
  NIKNumber: boolean;
  taxFile: boolean;
  NIKNumberMessage: string;
};

class Props {
  modelValue = prop<TaxSectionProp>({
    default: {
      NIK: "",
      NIKAddress: "",
      NIKName: "",
      taxNumber: "",
      taxName: "",
      taxAddress: "",
      attachment: "",
      attachmentUrl: "",
      fileNameAttachment: "",
      transactionCode: new OptionsClass(),
      isEfakturWithNik: false
    },
    required: true,
    type: Object
  });
  error = prop<TaxSectionErrorProp>({
    default: {
      taxNumber: false
    },
    required: true,
    type: Object
  });
  isMY = prop<boolean>({
    default: false
  });
  taxValidationConfig = prop<TaxValidationConfig | undefined>({
    default: undefined,
    required: false
  });
  isManagingCountryMY = prop<boolean>({
    default: false
  });
}

@Options({
  components: {
    UploadFile,
    Checkbox
  },
  watch: {
    taxValidationConfig: {
      handler() {
        const val = this.formModel.isEfakturWithNik
          ? this.formModel.NIK
          : this.formModel.taxNumber;
        if (val) {
          this.validateNIKOrNpwp(val);
        }
      },
      deep: true
    },
    isManagingCountryMY: {
      handler() {
        const val = this.formModel.isEfakturWithNik
          ? this.formModel.NIK
          : this.formModel.taxNumber;
        if (val) {
          this.validateNIKOrNpwp(val);
        }
      }
    },
    isMY: {
      handler() {
        const val = this.formModel.isEfakturWithNik
          ? this.formModel.NIK
          : this.formModel.taxNumber;
        if (val) {
          this.validateNIKOrNpwp(val);
        }
      }
    }
  }
})
class TaxSection extends Vue.with(Props) {
  transactionCodeList = optionTaxTransactionCode;

  get formModel() {
    return this.modelValue;
  }

  set formModel(val) {
    this.$emit("update:modelValue", val);
  }

  get formErrorModel() {
    return this.error;
  }

  set formErrorModel(val) {
    this.$emit("update:error", val);
  }

  get routeTypeName() {
    return this.$route.meta.form;
  }

  onKTPCheckChange(value: any) {
    const isChecked =
      typeof value === "boolean" ? value : !!this.formModel.isEfakturWithNik;
    this.formModel.isEfakturWithNik = isChecked;
    if (isChecked) {
      if (this.formModel.taxNumber) {
        this.formModel.NIK = this.formModel.taxNumber;
        this.modelValue.NIK = this.formModel.taxNumber;
      }
      if (this.formModel.taxName) {
        this.formModel.NIKName = this.formModel.taxName;
        this.modelValue.NIKName = this.formModel.taxName;
      }
      if (this.formModel.taxAddress) {
        this.formModel.NIKAddress = this.formModel.taxAddress;
        this.modelValue.NIKAddress = this.formModel.taxAddress;
      }
      if (this.formErrorModel.NIKNumber || this.formModel.NIK) {
        this.validateNIKOrNpwp(this.formModel.NIK || "");
      } else {
        this.formErrorModel.NIKNumberMessage = "";
      }
    } else {
      if (this.formModel.NIK) {
        this.formModel.taxNumber = this.formModel.NIK;
        this.modelValue.taxNumber = this.formModel.NIK;
      }
      if (this.formModel.NIKName) {
        this.formModel.taxName = this.formModel.NIKName;
        this.modelValue.taxName = this.formModel.NIKName;
      }
      if (this.formModel.NIKAddress) {
        this.formModel.taxAddress = this.formModel.NIKAddress;
        this.modelValue.taxAddress = this.formModel.NIKAddress;
      }
      if (this.formErrorModel.NIKNumber || this.formModel.taxNumber) {
        this.validateNIKOrNpwp(this.formModel.taxNumber || "");
      } else {
        this.formErrorModel.NIKNumberMessage = "";
      }
    }
  }

  validateNIKOrNpwp(value: string) {
    this.formModel.taxNumber = value;
    this.formModel.NIK = value;
    this.modelValue.taxNumber = value;
    this.modelValue.NIK = value;

    const isMY = this.isManagingCountryMY || this.isMY;
    const isKtp = this.formModel.isEfakturWithNik;
    const { valid, message } = validateTaxRegistration(
      value,
      this.taxValidationConfig,
      isMY,
      isKtp
    );

    this.formErrorModel.NIKNumber = !valid;
    this.formErrorModel.NIKNumberMessage = !valid
      ? (message || (isKtp ? "Masukan nomor KTP" : "Masukan Nomor NPWP"))
      : "";
  }

  changeKtpOrNpwpName(value: string) {
    this.formModel.taxName = value;
    this.formModel.NIKName = value;
    this.modelValue.taxName = value;
    this.modelValue.NIKName = value;
  }

  changeNikOrNpwpAddress(value: string) {
    this.formModel.taxAddress = value;
    this.formModel.NIKAddress = value;
    this.modelValue.taxAddress = value;
    this.modelValue.NIKAddress = value;
  }

  formatNumber(str: string) {
    const isMY = this.isManagingCountryMY || this.isMY;
    const numericOnly =
      this.taxValidationConfig && this.taxValidationConfig.validationEnabled
        ? this.taxValidationConfig.numericOnly
        : !isMY;

    return str
      .replace(numericOnly ? /\D+/g : /[^a-zA-Z0-9]/g, "")
      .slice(0, this.taxNumberMaxLength);
  }

  get taxNumberMaxLength() {
    // add dynamic max tax length based on config;
    const maxTaxNumberFallback = 16;
    if (
      !this.taxValidationConfig ||
      !this.taxValidationConfig.validationEnabled
    )
      return maxTaxNumberFallback;
    if (this.taxValidationConfig.maxLength !== 0)
      return this.taxValidationConfig.maxLength;
    return maxTaxNumberFallback;
  }
}

export default TaxSection;
